import { useState } from "react";
import { useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Container, Paper } from "@mui/material";
import {
  ConditionalRenderer,
  Header,
  SuccessCreation,
  useLanguageTranslator,
} from "../../../components";
import { PolicyInputData, PolicyEditor } from "../../../features";

import { useNotify } from "../../../components/@common/NotificationsProvider";
import { useAuthSession } from "../../../components/AuthSessionProvider";

const createPolicyMutationDocument = gql`
  mutation CreatePolicy($policyInputData: PolicyCreateInput!) {
    policyCreate(input: $policyInputData) {
      policy {
        id
        date
      }
    }
  }
`;

const PolicyNew = ({
  isNotPracticeCreateModule = true,
  onClosePopup = () => {},
  refetchFormOptions = () => {},
}: any) => {
  const [documentAttachmentDetails, setDocumentAttachmentDetails] =
    useState<any>(null);
  const [imageAttachmentDetails, setImageAttachmentDetails] =
    useState<any>(null);
  const [selectedListPractices, setSelectedListPractices] = useState<any>([]);
  const [selectedListPeople, setSelectedListPeople] = useState<any>([]);
  const [isOpen, setIsOpen] = useState({
    status: false,
    currId: null,
    module: "",
  });

  const notify = useNotify();
  const navigate = useNavigate();
  const { translator } = useLanguageTranslator();
  const { user } = useAuthSession() as any;
  const location = useLocation();

  const isAdmin = location?.pathname?.includes("admin");

  const [createPolicy, { loading }] = useMutation(
    createPolicyMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  /**
   * handleFinish()
   */

  const listOfPracticesIDs = selectedListPractices?.map(
    (item: { value: string }) => Number(item?.value)
  );

  const listOfPeopleIDs = selectedListPeople?.map((item: { value: string }) =>
    Number(item?.value)
  );

  const handleFinish = useCallback(
    async (data: PolicyInputData) => {
      try {
        const response = await createPolicy({
          variables: {
            policyInputData: {
              policyInput: {
                ...data,
                practices: listOfPracticesIDs,
                people: listOfPeopleIDs,
                ...(imageAttachmentDetails !== null
                  ? {
                      picture: {
                        source: imageAttachmentDetails?.base64URL,
                        originalFilename: imageAttachmentDetails?.name,
                      },
                    }
                  : {}),
                ...(documentAttachmentDetails !== null
                  ? {
                      document: {
                        source: documentAttachmentDetails?.base64URL,
                        originalFilename: documentAttachmentDetails?.name,
                      },
                    }
                  : {}),
              },
            },
          },
        });

        if (!isNotPracticeCreateModule) {
          onClosePopup();
          refetchFormOptions();
        } else {
          // navigate(`/${currentModule}/policies`);
          setIsOpen({
            status: true,
            currId: response?.data?.policyCreate?.policy?.id,
            module: "success",
          });
        }
      } catch (err) {
        notify.error(err as Error);
      }
    },
    [
      navigate,
      createPolicy,
      notify,
      imageAttachmentDetails,
      documentAttachmentDetails,
      listOfPracticesIDs,
      listOfPeopleIDs,
    ]
  );

  /**
   * handleCancel()
   */
  const handleCancel = useCallback(() => {
    !isNotPracticeCreateModule ? onClosePopup() : navigate(-1);
  }, [navigate]);

  return (
    <Box mb={!isNotPracticeCreateModule ? 0 : 24}>
      <SuccessCreation
        isOpen={isOpen}
        newCreationPath="/submission/policies/new"
        viewPath={isAdmin ? `/admin/policies/` : "/policies/"}
        module="Policy"
      />
      <ConditionalRenderer condition={isNotPracticeCreateModule}>
        <Header
          title={translator("Create new policies")}
          subtitle=""
        />
      </ConditionalRenderer>

      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 3 }}>
          <PolicyEditor
            disabled={loading}
            onFinish={handleFinish}
            onCancel={handleCancel}
            imageAttachmentDetails={imageAttachmentDetails}
            documentAttachmentDetails={documentAttachmentDetails}
            setImageAttachmentDetails={setImageAttachmentDetails}
            setDocumentAttachmentDetails={setDocumentAttachmentDetails}
            setSelectedListPractices={setSelectedListPractices}
            selectedListPractices={selectedListPractices}
            setSelectedListPeople={setSelectedListPeople}
            selectedListPeople={selectedListPeople}
            isNotPracticeCreateModule={isNotPracticeCreateModule}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export { PolicyNew };
